/**
 * Plan Formatters Utility
 *
 * Shared formatting functions for plan display across grid and table views.
 * All functions are memoized for performance.
 */

import type {
  BillingPeriod,
  EntityStatus,
  TargetAudience,
} from '@monorepo/shared/schemas/common'
import {
  BILLING_PERIOD,
  ENTITY_STATUS,
  TARGET_AUDIENCE,
} from '@monorepo/shared/schemas/common'
import type { Plan } from '../types/plan.types'

// Simple memoization utility
function memoize<T extends (...args: never[]) => unknown>(fn: T): T {
  const cache = new Map<string, ReturnType<T>>()
  return ((...args: Parameters<T>): ReturnType<T> => {
    const key = JSON.stringify(args)
    if (cache.has(key)) {
      return cache.get(key) as ReturnType<T>
    }
    const result = fn(...args) as ReturnType<T>
    cache.set(key, result)
    return result
  }) as T
}

/**
 * Get status badge color classes
 */
export const getStatusColor = memoize((status: EntityStatus): string => {
  switch (status) {
    case ENTITY_STATUS.active:
      return 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400'
    case ENTITY_STATUS.draft:
      return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400'
    case ENTITY_STATUS.archived:
      return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300'
    case ENTITY_STATUS.inactive:
      return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/20 dark:text-red-400'
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300'
  }
})

/**
 * Get status display label
 */
export const getStatusLabel = memoize((status: EntityStatus): string => {
  return status.charAt(0).toUpperCase() + status.slice(1)
})

/**
 * Get target audience badge color classes
 */
export const getTargetAudienceColor = memoize(
  (targetAudience: TargetAudience): string => {
    switch (targetAudience) {
      case TARGET_AUDIENCE.human:
        return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400'
      case TARGET_AUDIENCE.agent:
        return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/20 dark:text-green-400'
      case TARGET_AUDIENCE.both:
        return 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300'
    }
  }
)

/**
 * Get target audience display label
 */
export const getTargetAudienceLabel = memoize(
  (targetAudience: TargetAudience): string => {
    switch (targetAudience) {
      case TARGET_AUDIENCE.human:
        return 'Humans'
      case TARGET_AUDIENCE.agent:
        return 'Agents'
      case TARGET_AUDIENCE.both:
        return 'Both'
      default:
        return targetAudience
    }
  }
)

/**
 * Get category badge color classes
 */
export const getCategoryColor = memoize((category: string): string => {
  switch (category) {
    case 'plan':
      return 'bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-400'
    case 'addon':
      return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400'
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300'
  }
})

/**
 * Get category display label
 */
export const getCategoryLabel = memoize((category: string): string => {
  return category.charAt(0).toUpperCase() + category.slice(1)
})

/**
 * Format currency amount
 */
export function formatCurrency(
  amount: number,
  currency: string = 'USD'
): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

/**
 * Get price display for a plan
 * Returns { price, suffix } for rendering
 */
export function getPriceDisplay(plan: Plan): { price: string; suffix: string } {
  // Use pricing summary from backend if available
  if (plan.pricingSummary) {
    const { monthlyTotal, yearlyTotal, hasUsagePricing } = plan.pricingSummary

    // Determine which total to show based on plan's billing period
    const recurringTotal =
      plan.billing_period === BILLING_PERIOD.annual ? yearlyTotal : monthlyTotal
    const billingFrequency =
      plan.billing_period === BILLING_PERIOD.annual ? 'year' : 'month'

    // Handle different pricing scenarios
    if (recurringTotal === 0 && !hasUsagePricing) {
      return { price: 'Free', suffix: '' }
    }

    if (recurringTotal === 0 && hasUsagePricing) {
      return { price: 'Usage-based', suffix: '' }
    }

    const priceAmount = recurringTotal.toFixed(0)
    const usageSuffix = hasUsagePricing ? ' + Usage' : ''

    return {
      price: `$${priceAmount}`,
      suffix: `/${billingFrequency}${usageSuffix}`,
    }
  }

  // Fallback to old logic for backward compatibility
  let basePrice = 0

  // Add platform fee if it exists
  if (plan.platformFee) {
    basePrice += plan.platformFee
  }

  // Add seat pricing if enabled
  if (
    plan.seatFee?.enabled &&
    plan.seatFee.perSeatAmount &&
    plan.seatFee.minimumSeats
  ) {
    basePrice += plan.seatFee.perSeatAmount * plan.seatFee.minimumSeats
  }

  const billingFrequency =
    plan.billing_period === BILLING_PERIOD.annual ? 'year' : 'month'
  const hasUsage = (plan.meteredPrices?.length ?? 0) > 0

  if (basePrice === 0 && !hasUsage) {
    return { price: 'Free', suffix: '' }
  }

  if (basePrice === 0 && hasUsage) {
    return { price: 'Usage-based', suffix: '' }
  }

  const priceAmount = basePrice.toFixed(0)
  const usageSuffix = hasUsage ? ' + Usage' : ''

  return {
    price: `$${priceAmount}`,
    suffix: `/${billingFrequency}${usageSuffix}`,
  }
}

/**
 * Get billing period display label
 */
export const getBillingPeriodLabel = memoize(
  (billingPeriod: BillingPeriod): string => {
    switch (billingPeriod) {
      case BILLING_PERIOD.monthly:
        return 'Monthly'
      case BILLING_PERIOD.annual:
        return 'Annually'
      case BILLING_PERIOD.quarterly:
        return 'Quarterly'
      case BILLING_PERIOD.weekly:
        return 'Weekly'
      case BILLING_PERIOD.daily:
        return 'Daily'
      case BILLING_PERIOD.semi_annual:
        return 'Semi-Annual'
      default:
        return billingPeriod
    }
  }
)
